import { useEffect, useRef, useState } from 'react';
import { config, animated, useSpring } from '@react-spring/web';
import styles from './InlineTextLoading.module.css';

interface Props {
  testid?: string;
  onFirstDotEnded?: () => void;
  onSecondDotEnded?: () => void;
  onThirdDotEnded?: () => void;
}

const SPRING_CONFIG = {
  ...config.stiff,
  velocity: 0.005,
  clamp: true,
};

function buildConfig(direction: 'up' | 'down' | 'rest' | 'ended') {
  if (direction === 'rest' || direction === 'ended') {
    return;
  }

  if (direction === 'up') {
    return {
      from: {
        transform: `translate3d(0, 0px, 0)`,
      },
      to: {
        transform: `translate3d(0, -3px, 0)`,
      },
      config: SPRING_CONFIG,
    };
  }

  return {
    from: {
      transform: `translate3d(0, -3px, 0)`,
    },
    to: {
      transform: `translate3d(0, 0px, 0)`,
    },
    config: SPRING_CONFIG,
  };
}

type SpringState = 'up' | 'down' | 'rest' | 'ended';

export function InlineTextLoading(props: Props) {
  const { testid, onFirstDotEnded, onSecondDotEnded, onThirdDotEnded } = props;
  const [direction, setDirection] = useState<SpringState>('up');
  const [secondSpringDirection, setSecondSpringDirection] =
    useState<SpringState>('rest');
  const [thirdSpringDirection, setThirdSpringDirection] =
    useState<SpringState>('rest');
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (thirdSpringDirection !== 'ended') {
      return;
    }
    const timeout = setTimeout(() => {
      setDirection('up');
      setSecondSpringDirection('rest');
      setThirdSpringDirection('rest');
    }, 200);

    return () => {
      clearTimeout(timeout);
    };
  }, [thirdSpringDirection]);

  const firstSpring = useSpring({
    ...buildConfig(direction),
    onRest() {
      const { current: isMounted } = mounted;

      if (isMounted === false) {
        return;
      }

      if (direction === 'up') {
        setSecondSpringDirection('up');
        setDirection('down');
      } else if (direction === 'down') {
        if (onFirstDotEnded !== undefined) {
          onFirstDotEnded();
        }
        if (mounted.current === false) {
          return;
        }
        setDirection('ended');
      }
    },
  });

  const secondSpring = useSpring({
    ...buildConfig(secondSpringDirection),
    onRest() {
      const { current: isMounted } = mounted;
      if (isMounted === false) {
        return;
      }

      if (secondSpringDirection === 'up') {
        setSecondSpringDirection('down');
        setThirdSpringDirection('up');
      } else if (secondSpringDirection === 'down') {
        if (onSecondDotEnded !== undefined) {
          onSecondDotEnded();
        }
        if (mounted.current === false) {
          return;
        }
        setSecondSpringDirection('ended');
      }
    },
  });

  const thirdSpring = useSpring({
    ...buildConfig(thirdSpringDirection),
    onRest() {
      const { current: isMounted } = mounted;
      if (isMounted === false) {
        return;
      }

      if (thirdSpringDirection === 'up') {
        setThirdSpringDirection('down');
      } else if (thirdSpringDirection === 'down') {
        if (onThirdDotEnded !== undefined) {
          onThirdDotEnded();
        }
        if (mounted.current === false) {
          return;
        }
        setThirdSpringDirection('ended');
      }
    },
  });

  return (
    <span className={styles.container} data-testid={testid}>
      <animated.div style={firstSpring}>.</animated.div>
      <animated.div style={secondSpring}>.</animated.div>
      <animated.div style={thirdSpring}>.</animated.div>
    </span>
  );
}
